import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { GameState, Player } from "@/types/poker";

interface PokerActionDockProps {
  state: GameState | null;
  you: Player | undefined;
  toCall: number;
  onAct: (action: string, amount?: number) => void;
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

export const PokerActionDock = ({ state, you, toCall, onAct }: PokerActionDockProps) => {
  const { t } = useTranslation();
  // THE SACRED STATE: This is the TOTAL bet amount for the round. This is the single source of truth.
  const [amount, setAmount] = useState(0);

  const isMyTurn = state?.players[state.actionIndex ?? -1]?.id === you?.id && !you?.folded;

  // --- DERIVED VALUES & MEMOS ---
  // All calculated values are derived here to avoid duplicate logic.
  const { legalActions, minBet, yourCurrentBet, maxAmount, minRaiseTotal, potSize } = useMemo(() => {
    const actions: { [key: string]: boolean } = {};
    if (!isMyTurn || !you || !state) {
      return { legalActions: actions, minBet: 20, yourCurrentBet: 0, maxAmount: 0, minRaiseTotal: 40, potSize: 0 };
    }

    actions.fold = true;
    if (toCall === 0) {
      actions.check = true;
      if (you.stack > 0) actions.bet = true;
    } else {
      if (you.stack >= toCall) actions.call = true;
      if (you.stack > toCall) actions.raise = true;
    }

    const ycb = state.roundBets?.[you.id] ?? 0;
    const mb = state.bb ?? 20;
    const ma = you.stack + ycb;
    const mrt = state.currentBet + (state.minRaise ?? mb);
    const ps = state.pot ?? 0;

    return { legalActions: actions, minBet: mb, yourCurrentBet: ycb, maxAmount: ma, minRaiseTotal: mrt, potSize: ps};
  }, [isMyTurn, you, state, toCall]);

  // --- THE NORMALIZATION FUNCTION ---
  // The only function allowed to set the `amount` state. It ensures the value is always valid and synchronized.
  const updateAmount = (newAmount: number) => {
    let val = newAmount;

    // 1. If user tries to raise for less than the min raise, snap them to the min raise.
    if (val > (toCall + yourCurrentBet) && val < minRaiseTotal) {
      // Allow all-in even if it's not a full raise
      if (val < maxAmount) {
         val = minRaiseTotal;
      }
    }

    // 2. Clamp the value to what's possible (from a call up to all-in).
    val = clamp(val, (toCall + yourCurrentBet), maxAmount);

    // 3. Round the value to the nearest step (minBet), unless it's an all-in.
    if (val < maxAmount) {
      val = Math.round(val / minBet) * minBet;
    }

    // 4. A final clamp in case rounding went out of bounds and set the final state.
    setAmount(clamp(val, (toCall + yourCurrentBet), maxAmount));
  };


  // --- EFFECTS ---
  // When the turn starts, preset the amount to the exact call amount.
  useEffect(() => {
    if (isMyTurn) {
      setAmount(toCall + yourCurrentBet);
    }
  }, [isMyTurn, toCall, yourCurrentBet]);


  // --- HANDLERS ---
  const handleActionClick = () => {
    if (!you || !state) return;

    if (amount === (toCall + yourCurrentBet)) {
      if (toCall === 0) onAct("check");
      else onAct(you.stack > toCall ? "call" : "call-allin");
    } else { // This is a bet or a raise
      if (amount >= maxAmount) {
        onAct("allin", maxAmount);
      } else {
        if (legalActions.bet) {
          onAct("bet", amount);
        } else { // Must be a raise
          // The backend now expects the TOTAL bet amount.
          onAct("raise", amount);
        }
      }
    }
  };

  const getButtonDisplay = () => {
    if (!isMyTurn || !you) return { text: "...", variant: "secondary" as const };

    if (amount === (toCall + yourCurrentBet)) {
      if (toCall === 0) return { text: t('poker_check'), variant: "secondary" as const };
      return { text: t('poker_call_amount', { amount: toCall }), variant: "secondary" as const };
    }
    if (amount >= maxAmount) {
      return { text: t('poker_allin_amount', { amount: maxAmount }), variant: "primary" as const };
    }
    if (legalActions.bet) {
      return { text: t('poker_bet_amount', { amount }), variant: "primary" as const };
    }
    return { text: t('poker_raise_to', { amount }), variant: "primary" as const };
  };

  const showControls = legalActions.bet || legalActions.raise;
  const buttonDisplay = getButtonDisplay();

  return (
    <div className={cn(
        "h-[180px] space-y-3 rounded-2xl border border-white/10 bg-black/20 p-4 transition-opacity",
        !isMyTurn && "opacity-50 pointer-events-none"
      )}>
      {showControls ? (
        <div className="space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              type="number"
              value={amount}
              min={toCall + yourCurrentBet}
              max={maxAmount}
              step={minBet}
              className="bg-black/40 text-white"
              onChange={(e) => updateAmount(Number(e.target.value) || 0)}
            />
            <Slider
              min={toCall + yourCurrentBet}
              max={maxAmount}
              step={minBet}
              value={[amount]}
              onValueChange={(value) => updateAmount(value[0])}
            />
          </div>
          <div className="flex items-center justify-center gap-2">
            <Button size="sm" variant="outline" className="h-8 flex-1 bg-black/30 text-white/70 hover:bg-black/50" onClick={() => updateAmount(toCall + yourCurrentBet + potSize * 0.5)}>{t('poker_pot_half')}</Button>
            <Button size="sm" variant="outline" className="h-8 flex-1 bg-black/30 text-white/70 hover:bg-black/50" onClick={() => updateAmount(toCall + yourCurrentBet + potSize * 0.75)}>{t('poker_pot_3_4')}</Button>
            <Button size="sm" variant="outline" className="h-8 flex-1 bg-black/30 text-white/70 hover:bg-black/50" onClick={() => updateAmount(maxAmount)}>{t('poker_allin')}</Button>
          </div>
        </div>
      ) : (
        <div className="flex h-[76px] items-center justify-center text-center text-sm text-muted-foreground">
          {isMyTurn ? t('poker_your_action') : t('poker_waiting')}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="lg" onClick={() => onAct("fold")} className="flex-1" variant="destructive" disabled={!isMyTurn || !legalActions.fold}>
          {t('poker_fold')}
        </Button>
        <Button size="lg" onClick={handleActionClick} className="flex-1 min-w-[120px]" variant={buttonDisplay.variant} disabled={!isMyTurn}>
          {buttonDisplay.text}
        </Button>
      </div>
    </div>
  );
};
